import { api } from "../lib/api";
import { useEngineStore } from "../store/useEngineStore";
import {
  getMe,
  loginInteractive,
  logout as doLogout,
  resumeIfAuthorized,
} from "../tg/auth";
import { connect, getExistingClient } from "../tg/tgClient";
import { enumerateDialogs } from "../tg/enumeration";
import { sessionStore } from "../tg/sessionStore";
import type { TelegramClient } from "telegram";

type Resolver = (v: string) => void;

class MTProtoEngine {
  private codeResolver: Resolver | null = null;
  private passwordResolver: Resolver | null = null;

  async init(): Promise<void> {
    const st = useEngineStore.getState();
    try {
      const ok = await resumeIfAuthorized();
      if (ok) {
        st.setPhase("connected");
        const me = await getMe();
        st.setMe(me);
        if (me) await api.putAccount({ tgUserId: me.id, username: me.username });
      } else {
        st.setPhase("disconnected");
      }
    } catch (e) {
      st.setPhase("disconnected");
      st.setError(String(e));
    }
  }

  async setCredentials(apiId: number, apiHash: string): Promise<void> {
    await sessionStore.putCreds(apiId, apiHash);
  }

  async startLogin(phone: string): Promise<void> {
    const st = useEngineStore.getState();
    st.setError(null);
    st.setPhase("connecting");
    try {
      await connect();
      await loginInteractive({
        getPhone: async () => phone,
        getCode: () =>
          new Promise<string>((res) => {
            this.codeResolver = res;
            useEngineStore.getState().setPhase("awaitCode");
          }),
        getPassword: () =>
          new Promise<string>((res) => {
            this.passwordResolver = res;
            useEngineStore.getState().setPhase("awaitPassword");
          }),
        onError: (e) => useEngineStore.getState().setError(String(e)),
      });
      const me = await getMe();
      st.setMe(me);
      st.setPhase("connected");
      if (me) await api.putAccount({ tgUserId: me.id, username: me.username });
    } catch (e) {
      st.setPhase("error");
      st.setError(String(e));
    }
  }

  submitCode(code: string): void {
    this.codeResolver?.(code);
    this.codeResolver = null;
  }
  submitPassword(pw: string): void {
    this.passwordResolver?.(pw);
    this.passwordResolver = null;
  }

  async logout(): Promise<void> {
    await doLogout();
    const st = useEngineStore.getState();
    st.setMe(null);
    st.setPhase("disconnected");
    st.setError(null);
  }

  async ensureClient(): Promise<TelegramClient> {
    const { client } = await connect();
    return client;
  }

  async enumerate(signal?: AbortSignal): Promise<{ active: number; deactivated: number }> {
    const st = useEngineStore.getState();
    const client = getExistingClient() ?? (await this.ensureClient());
    st.setEnumProgress({ seen: 0, currentTitle: "" });
    try {
      const res = await enumerateDialogs(
        client,
        (p) => st.setEnumProgress(p),
        signal,
      );
      return res;
    } finally {
      st.setEnumProgress(null);
    }
  }
}

export const engine = new MTProtoEngine();
